import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BookingResponse from "@/model/BookingResponse";
import formatPrice from "@/utils/formatPrice";
import isoDate from "@/utils/isoDate";
import guestsUtils from "@/utils/guests";
import "@/components/BookingsTable/BookingsTable.scss";

interface BookingsTableProps {
  bookings: BookingResponse[];
  showProperty: boolean;
  cancellingId: string | null;
  onCancel: (booking: BookingResponse) => void;
  /** Id of the request being accepted or declined, if any. */
  answeringId?: string | null;
  onAccept?: (booking: BookingResponse) => void;
  onDecline?: (booking: BookingResponse) => void;
}

function isUpcoming(booking: BookingResponse): boolean {
  return Boolean(
    booking.checkIn && booking.checkIn > isoDate.toIsoDate(new Date())
  );
}

function BookingsTable({
  bookings,
  showProperty,
  cancellingId,
  onCancel,
  answeringId = null,
  onAccept,
  onDecline,
}: BookingsTableProps) {
  const { t, i18n } = useTranslation();

  return (
    <table className="bookings-table">
      <thead>
        <tr>
          {showProperty && <th>{t("bookings.columns.property")}</th>}
          <th>{t("bookings.columns.guest")}</th>
          <th>{t("bookings.columns.checkIn")}</th>
          <th>{t("bookings.columns.checkOut")}</th>
          <th>{t("bookings.columns.guests")}</th>
          <th>{t("bookings.columns.total")}</th>
          <th>{t("bookings.columns.status")}</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {bookings.map((booking, index) => {
          const guestName = [booking.guestFirstName, booking.guestLastName]
            .filter(Boolean)
            .join(" ");
          const ownerBlock = booking.type === "OWNER_BLOCK";
          const external = booking.type === "EXTERNAL";
          const refunded = booking.refundedAmount ?? 0;
          // Imported stays are cancelled on their channel; the calendar sync follows.
          const canCancel =
            booking.id &&
            !external &&
            booking.status === "CONFIRMED" &&
            isUpcoming(booking);
          const rowClassName = [
            "bookings-table__row",
            booking.status === "CANCELLED" ? "bookings-table__row--cancelled" : "",
            external && booking.externalConflict && booking.status === "CONFIRMED"
              ? "bookings-table__row--conflict"
              : "",
            booking.status === "AWAITING_HOST" ? "bookings-table__row--request" : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <tr
              key={booking.id ?? index}
              className={rowClassName}
            >
              {showProperty && (
                <td>
                  {booking.propertyId ? (
                    <Link to={`/properties/${booking.propertyId}`}>
                      {booking.propertyTitle ?? t("bookings.viewProperty")}
                    </Link>
                  ) : (
                    (booking.propertyTitle ?? "—")
                  )}
                </td>
              )}
              <td>
                {ownerBlock && (
                  <span className="bookings-table__block">
                    {t("bookings.ownerBlock")}
                  </span>
                )}
                {external && (
                  <>
                    <span className="bookings-table__block bookings-table__block--external">
                      {t("bookings.external", {
                        source: booking.externalSource ?? "iCal",
                      })}
                    </span>
                    {booking.externalLabel && (
                      <div className="bookings-table__muted">
                        {booking.externalLabel}
                      </div>
                    )}
                    {booking.externalConflict && booking.status === "CONFIRMED" && (
                      <div className="bookings-table__conflict">
                        {t("bookings.externalConflict")}
                      </div>
                    )}
                  </>
                )}
                {!ownerBlock && !external && (
                  <>
                    <div>{guestName || "—"}</div>
                    {booking.guestEmail && (
                      <div className="bookings-table__muted">
                        {booking.guestEmail}
                      </div>
                    )}
                  </>
                )}
              </td>
              <td>{isoDate.formatIsoDate(booking.checkIn, i18n.language)}</td>
              <td>{isoDate.formatIsoDate(booking.checkOut, i18n.language)}</td>
              <td>
                <div>{external ? "—" : (booking.guestCount ?? "—")}</div>
                {(guestsUtils.ofBooking(booking).children > 0 ||
                  guestsUtils.ofBooking(booking).infants > 0) && (
                  <div className="bookings-table__muted">
                    {guestsUtils.describe(t, guestsUtils.ofBooking(booking))}
                  </div>
                )}
              </td>
              <td>
                <div>
                  {ownerBlock || external
                    ? "—"
                    : formatPrice(booking.totalAmount, booking.currency)}
                </div>
                <div className="bookings-table__muted">
                  {t("bookings.nights", { count: booking.nights ?? 0 })}
                </div>
                {refunded > 0 && (
                  <div className="bookings-table__muted">
                    {t("bookings.refunded", {
                      amount: formatPrice(refunded, booking.currency),
                    })}
                  </div>
                )}
                {booking.receiptUrl && (
                  <a
                    className="bookings-table__receipt"
                    href={booking.receiptUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("bookings.receipt")}
                  </a>
                )}
              </td>
              <td>
                {booking.status ? (
                  <span
                    className={`status-badge status-badge--${booking.status.toLowerCase()}`}
                  >
                    {t(`bookings.status.${booking.status}`)}
                  </span>
                ) : (
                  "—"
                )}
                {booking.status === "AWAITING_HOST" && booking.requestExpiresAt && (
                  <div className="bookings-table__deadline">
                    {t("bookings.request.answerBy", {
                      deadline: new Date(booking.requestExpiresAt).toLocaleString(i18n.language, {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      }),
                    })}
                  </div>
                )}
                {booking.status === "DECLINED" && booking.declineReason && (
                  <div className="bookings-table__muted">
                    {t(`bookings.declineReason.${booking.declineReason}`)}
                  </div>
                )}
              </td>
              <td>
                {booking.status === "AWAITING_HOST" && booking.id && onAccept && onDecline && (
                  <div className="bookings-table__answer">
                    <button
                      type="button"
                      className="bookings-table__accept"
                      onClick={() => onAccept(booking)}
                      disabled={answeringId === booking.id}
                    >
                      {t("bookings.request.accept")}
                    </button>
                    <button
                      type="button"
                      className="bookings-table__decline"
                      onClick={() => onDecline(booking)}
                      disabled={answeringId === booking.id}
                    >
                      {t("bookings.request.decline")}
                    </button>
                  </div>
                )}
                {canCancel && (
                  <button
                    type="button"
                    className="bookings-table__cancel"
                    onClick={() => onCancel(booking)}
                    disabled={cancellingId === booking.id}
                  >
                    {cancellingId === booking.id
                      ? t("bookings.cancelling")
                      : t("bookings.cancelButton")}
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default BookingsTable;
export type { BookingsTableProps };
